// models/ReportTypeNames.js 
import { formatDisplayName, getIconClass, getIconLetter } from './ReportTypeDesignHelper';
export class ReportTypeNames {
  constructor(list = []) {
    // New API returns a flat array of objects with fields:
    // { id, name, description, specialty, isFavorite, isEnterprise, isEnterpriseEnabled,
    //   isPersonal, isDefault, customInstruction, displayOrder? }
    // Normalize into an internal map by name and a favorites name list ordered by displayOrder
    const byName = new Map();
    const favoritesWithOrder = [];

    (Array.isArray(list) ? list : []).forEach((item) => {
      if (!item || !item.name) return;
      const normalized = {
        id: item.id || null,
        name: item.name,
        description: item.description || undefined,
        specialty: item.specialty || null,
        isFavorite: !!item.isFavorite,
        isEnterprise: !!item.isEnterprise,
        isEnterpriseEnabled: item.isEnterpriseEnabled !== undefined ? item.isEnterpriseEnabled : null,
        isPersonal: !!item.isPersonal,
        isDefault: !!item.isDefault,
        customInstruction: item.customInstruction || null,
        displayOrder: typeof item.displayOrder === 'number' ? item.displayOrder : undefined
      };
      if (!byName.has(normalized.name)) {
        byName.set(normalized.name, normalized);
        if (normalized.isFavorite) favoritesWithOrder.push({ name: normalized.name, order: normalized.displayOrder });
      }
    });

    // Sort favorites by provided displayOrder (fallback to stable order at the end)
    favoritesWithOrder.sort((a, b) => {
      const oa = typeof a.order === 'number' ? a.order : Number.POSITIVE_INFINITY;
      const ob = typeof b.order === 'number' ? b.order : Number.POSITIVE_INFINITY;
      return oa - ob;
    });

    this.byName = byName; // Map<string, Type>
    this.types = Array.from(byName.values()); // Array<Type>
    this.favorites = favoritesWithOrder.map(f => f.name); // Array<string> in display order

    // Ensure flags and displayOrder fields are consistent
    this.syncFavoriteFlags();
  }

  // Internal helpers to sync flags with favorites list
  syncFavoriteFlags() {
    const favoriteSet = new Set(this.favorites);
    this.types.forEach(t => {
      t.isFavorite = favoriteSet.has(t.name);
      t.displayOrder = t.isFavorite ? this.favorites.indexOf(t.name) : undefined;
    });
  }

  // Presence check
  hasType(name) {
    return this.byName.has(name);
  }

  // Determine whether the provided report type belongs to an enterprise category
  isEnterprise(typeName) {
    const t = this.byName.get(typeName);
    return !!t?.isEnterprise;
  }

  // Get all report types (with isFavorite flag)
  getAll() {
    return this.types;
  }

  // Get just the names array
  getNames() {
    return this.types.map(t => t.name);
  }

  // Get favorite names only (in current order)
  getFavorites() {
    return [...this.favorites];
  }

  // Get favorite report types (objects with full info)
  getFavoriteTypes() {
    const set = new Set(this.favorites);
    return this.types.filter(t => set.has(t.name));
  }

  // Get favorites with display order
  getFavoritesWithOrder() {
    return this.favorites.map((name) => ({
      name,
      description: this.byName.get(name)?.description,
      displayOrder: this.byName.get(name)?.displayOrder ?? this.favorites.indexOf(name)
    }));
  }

  // Check if a report type is favorite
  isFavorite(name) {
    return this.favorites.includes(name);
  }

  // Toggle favorite status
  toggleFavorite(name) {
    if (!name || typeof name !== 'string') return false;
    const exists = this.favorites.includes(name);
    if (!exists) {
      // add to end only if the type exists
      if (this.byName.has(name)) {
        this.favorites.push(name);
      } else {
        // Ignore toggling for unknown names to avoid empty favorites
        return false;
      }
    } else {
      // remove
      this.favorites = this.favorites.filter(n => n !== name);
    }
    this.syncFavoriteFlags();
    return !exists;
  }

  // Move favorite up in order
  moveFavoriteUp(index) {
    if (index <= 0 || index >= this.favorites.length) return false;
    const favs = this.favorites;
    [favs[index - 1], favs[index]] = [favs[index], favs[index - 1]];
    this.syncFavoriteFlags();
    return true;
  }

  // Move favorite down in order
  moveFavoriteDown(index) {
    if (index < 0 || index >= this.favorites.length - 1) return false;
    const favs = this.favorites;
    [favs[index], favs[index + 1]] = [favs[index + 1], favs[index]];
    this.syncFavoriteFlags();
    return true;
  }

  // Set favorites from external source (for cancel operation). Accepts array of names
  setFavorites(newFavorites) {
    this.favorites = (newFavorites || []).map(name => (typeof name === 'string' ? name : name?.name)).filter(Boolean);
    this.syncFavoriteFlags();
  }

  // Update favorites with new order (array of { name, description? })
  updateFavoritesOrder(orderedFavorites) {
    this.favorites = orderedFavorites.map(f => f.name);
    this.syncFavoriteFlags();
  }

  // Build filtered types for display (excluding favorites) with optional category filter
  getFilteredTypes(searchTerm = '', categoryFilter = 'all') {
    const favoriteSet = new Set(this.favorites);

    let filtered = this.types.filter(t => !favoriteSet.has(t.name));

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(s) || (t.description?.toLowerCase().includes(s))
      );
    }

    if (categoryFilter !== 'all') {
      const filterFn = {
        'enterprise': (t) => t.isEnterprise,
        'personal': (t) => t.isPersonal,
        'default': (t) => t.isDefault
      }[categoryFilter];
      if (filterFn) filtered = filtered.filter(filterFn);
    }

    return filtered;
  }

  // Get a primary category label for a type for filtering and badges
  getCategoryForType(typeName) {
    if (this.isFavorite(typeName)) return 'favorites';
    const t = this.byName.get(typeName);
    if (!t) return 'default';
    if (t.isPersonal) return 'personal';
    if (t.isEnterprise) return 'enterprise';
    if (t.isDefault) return 'default';
    return 'default';
  }

  // Get category badge info for a type
  getCategoryBadgeInfo(typeName) {
    if (this.isEnterprise(typeName)) {
      return {
        text: ' ',
        className: 'badge-enterprise'
      };
    }
    return null;
  }

  // Get specialty for a type
  getSpecialty(typeName) {
    const t = this.byName.get(typeName);
    return t?.specialty || null;
  }

  // Check if enterprise type is enabled
  isEnterpriseEnabled(typeName) {
    const t = this.byName.get(typeName);
    return t?.isEnterpriseEnabled ?? null;
  }

  // Icon helpers
  static getIconClass(typeName) {
    return getIconClass(typeName);
  }

  static getIconLetter(typeName) {
    return getIconLetter(typeName);
  }

  static formatDisplayName(typeName) {
    return formatDisplayName(typeName);
  }

  // Add a new report type locally
  addType(name, description = null, isFavorite = false) {
    if (this.byName.has(name)) return false;
    const newType = {
      id: null,
      name,
      description: description || undefined,
      isFavorite: !!isFavorite,
      isEnterprise: false,
      isPersonal: true,
      isDefault: false,
      customInstruction: null,
      displayOrder: undefined
    };
    this.byName.set(name, newType);
    this.types.push(newType);
    if (isFavorite) this.favorites.push(name);
    this.syncFavoriteFlags();
    return true;
  }

  // Update a report type's description
  updateType(name, description) {
    const t = this.byName.get(name);
    if (!t) return false;
    if (description) t.description = description; else delete t.description;
    return true;
  }

  // Remove a report type (only if personal)
  removeType(name) {
    const t = this.byName.get(name);
    if (!t || !t.isPersonal) return false;
    this.byName.delete(name);
    this.types = this.types.filter(x => x.name !== name);
    this.favorites = this.favorites.filter(n => n !== name);
    this.syncFavoriteFlags();
    return true;
  }

  // Get type details including description
  getTypeDetails(name) {
    const t = this.byName.get(name);
    if (t) {
      return {
        name: t.name,
        description: t.description,
        isFavorite: t.isFavorite,
        category: this.getCategoryForType(t.name)
      };
    }
    return null;
  }

  // Export data compatible with previous usage: a minimal snapshot
  exportData() {
    return this.types.map((t, index) => ({
      name: t.name,
      description: t.description,
      specialty: t.specialty,
      isFavorite: t.isFavorite,
      isEnterprise: t.isEnterprise,
      isEnterpriseEnabled: t.isEnterpriseEnabled,
      isPersonal: t.isPersonal,
      isDefault: t.isDefault,
      displayOrder: this.favorites.indexOf(t.name) !== -1 ? this.favorites.indexOf(t.name) : index
    }));
  }
}